/**
 * Single-flight guard + resume ledger for the wastage submission (T1.8).
 *
 * WHY THIS EXISTS
 * ---------------
 * Recording wastage is a SERIAL loop of one POST per item, with no transaction spanning
 * the loop. Every POST that succeeds has already written a StockOutRecord and deducted
 * stock via FIFO. That makes two things dangerous:
 *
 *   1. Concurrent entry. Three code paths call handleSubmit — the Confirm button, the
 *      shortage modal's "Confirm & Create Negative Stock", and the return trip from the
 *      recipe editor. `isSubmitting` is React state, so it does not update before a fast
 *      second click is handled, and the same items got posted twice.
 *
 *   2. Retry-from-zero. When the loop failed on item 5, the retry restarted at item 1 —
 *      re-recording items 1-4 and deducting their stock a second time.
 *
 * The ledger fixes (2) by remembering which item keys the server has CONFIRMED recorded,
 * so a retry resumes instead of restarting. `tryBegin`/`end` fix (1).
 *
 * Deliberately a plain object rather than a hook: it is held in a ref, must survive
 * re-renders untouched, and is unit-testable without rendering a 4,700-line component.
 *
 * NOTE: Phase 3 replaces the serial loop with an atomic, idempotency-keyed
 * POST /api/wastage/batch, which makes the resume ledger redundant. The single-flight
 * guard stays regardless — it is cheap and it is the last defence against a double click.
 */

export function createSubmissionLedger() {
  let inFlight = false;
  const recorded = new Set();

  return {
    /**
     * Claim the right to run a submission.
     * @returns {boolean} true if the caller may proceed; false if one is already running.
     */
    tryBegin() {
      if (inFlight) return false;
      inFlight = true;
      return true;
    },

    /** Release the claim. Always call from a `finally`. */
    end() {
      inFlight = false;
    },

    /** @returns {boolean} whether a submission is currently running. */
    isInFlight() {
      return inFlight;
    },

    /**
     * Mark an item as confirmed-recorded by the server. Call IMMEDIATELY after a
     * successful response and before anything else that can throw, so a later failure
     * in the same loop cannot cause a retry to re-post it.
     */
    markRecorded(itemKey) {
      recorded.add(String(itemKey));
    },

    /** @returns {boolean} whether this item was already recorded in this submission set. */
    wasRecorded(itemKey) {
      return recorded.has(String(itemKey));
    },

    /** Items from `items` that a previous attempt already recorded. */
    partitionPending(items, keyOf = (i) => i) {
      const pending = [];
      const skipped = [];
      for (const item of items || []) {
        (recorded.has(String(keyOf(item))) ? skipped : pending).push(item);
      }
      return { pending, skipped };
    },

    /**
     * Forget everything. Call ONLY on a fully successful submission or an explicit form
     * reset — never on a partial failure, which is exactly when the memory matters.
     */
    reset() {
      recorded.clear();
    },

    /** For assertions and logging. */
    size() {
      return recorded.size;
    }
  };
}

export default createSubmissionLedger;
